// Words, colours and numbers the stock pages share (the stock module, 22 Oct
// 2026). Item names are the floor's own (Slovenian, from the director's
// Excel); everything around them is in the app's English.

export const TEAL = '#14b8a6';
export const AMBER = '#f59e0b';
// Packaging's own colour (the director, 27 Oct 2026): amber is what can be
// made, and what needs counting -- a tag should not look like a warning.
export const YELLOW = '#fde047';
export const RED = '#f87171';
export const SKY = '#38bdf8';
export const VIOLET = '#a78bfa';
export const GREEN = '#4ade80';
export const SLATE = '#94a3b8';
export const BORD = '#2a3040';
export const CARD = '#1e2329';
export const SUNK = '#161b22';
export const DARK = '#0f1117';
export const MUTED = '#6b7280';
export const DIM = '#9ca3af';
export const TEXT = '#f0f2f5';

// What an item is.
export const KINDS = [
  { key: 'finished',  label: 'Finished product', color: GREEN },
  { key: 'stage',     label: 'Assembly stage',   color: VIOLET },
  { key: 'moulded',   label: 'Moulded here',     color: TEAL },
  // Made here, without a mould (cut, sewn), and From customer: 26 Oct 2026.
  { key: 'made',      label: 'Made here',        color: '#f472b6' },
  { key: 'bought',    label: 'Bought in',        color: SKY },
  { key: 'customer',  label: 'From customer',    color: '#fb923c' },
  { key: 'packaging', label: 'Packaging',        color: YELLOW },
  { key: 'material',  label: 'Material',         color: SLATE },
];
export const kindOf = (k) => KINDS.find(x => x.key === k) || { key: k, label: k, color: DIM };

// What happened. sign: what it does to the figure; counted sets it.
export const MOVES = {
  used:       { label: 'Used',       icon: 'fa-arrow-down',            color: AMBER,  sign: -1 },
  received:   { label: 'Received',   icon: 'fa-arrow-up',              color: GREEN,  sign: 1 },
  counted:    { label: 'Counted',    icon: 'fa-clipboard-check',       color: SKY,    sign: 0 },
  made:       { label: 'Made',       icon: 'fa-screwdriver-wrench',    color: VIOLET, sign: 1 },
  scrapped:   { label: 'Scrapped',   icon: 'fa-trash',                 color: RED,    sign: -1 },
  chamfered:  { label: 'Pofrezkane', icon: 'fa-arrows-left-right',     color: TEAL,   sign: 0 },
  correction: { label: 'Correction', icon: 'fa-pen',                   color: SLATE,  sign: 1 },
  // Switched on by the director in Stock → Automatic stock usage (28 Oct 2026, migration 085).
  pallet:     { label: 'Pallet packed', icon: 'fa-pallet',             color: '#fde047', sign: 0 },
  sent_out:   { label: 'Sent out',   icon: 'fa-truck-arrow-right',     color: '#fb923c', sign: 0 },
  back:       { label: 'Back',       icon: 'fa-truck-ramp-box',        color: GREEN,  sign: 1 },
};
// The ones offered when recording, in this order.
export const RECORD_KINDS = ['used', 'received', 'counted', 'made', 'scrapped'];

export const num = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('en-GB', { maximumFractionDigits: 3 }));

// "1,950 kos", and "= 39 bala" where it has a pack.
export const qty = (n, item) => `${num(n)} ${item?.unit || ''}`.trim();
export const packs = (n, item) => (item?.pack_unit && Number(item.pack_qty) > 0
  ? `${num(Math.round((Number(n) / Number(item.pack_qty)) * 100) / 100)} ${item.pack_unit}` : null);

// How much of an input one item takes: "2", or "1 per 30" for a box per thirty.
export const perOne = (q) => {
  const n = Number(q);
  if (n > 0 && n < 1 && Math.abs(1 / n - Math.round(1 / n)) < 0.01) return `1 per ${Math.round(1 / n)}`;
  return num(Math.round(n * 1000) / 1000);
};
// The same, as it is typed into the box: "1/30" or "2".
export const perOneTyped = (q) => {
  const n = Number(q);
  if (n > 0 && n < 1 && Math.abs(1 / n - Math.round(1 / n)) < 0.01) return `1/${Math.round(1 / n)}`;
  return String(Math.round(n * 1000) / 1000);
};

export const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
export const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// A stock figure after a movement, worked out on the screen for the preview
// -- the same arithmetic as the server's (backend utils/stock.js).
export function afterMove(level, m, chamfer) {
  const q = Number(m.qty) || 0;
  const raw = chamfer ? Number(level?.raw) || 0 : 0;
  const done = chamfer ? Number(level?.done) || 0 : 0;
  let all = chamfer ? raw + done : Number(level?.total) || 0;
  let r = raw, d = done;
  const add = (v) => { if (!chamfer) all += v; else if (m.chamfered) d += v; else r += v; };
  switch (m.kind) {
    case 'counted':   if (!chamfer) all = q; else if (m.chamfered) d = q; else r = q; break;
    case 'received':
    case 'made':
    case 'back':      add(q); break;
    case 'used':
    case 'scrapped':  add(-q); break;
    case 'chamfered': r -= q; d += q; break;
    default: break;
  }
  return chamfer ? { total: r + d, raw: r, done: d } : { total: all };
}

// The link a kind needs before the app can use the item (29 Oct 2026: Tray
// for Dose was counted, but no BOM could find it). null when it has it.
const NEEDS_LINK = {
  packaging: { field: 'packaging_id', to: 'Packaging', pick: 'pick it under "Which item in Admin → Packaging is it?"', why: 'BOMs, Made and pallets cannot find it' },
  moulded: { field: 'part_id', to: 'a part', pick: 'pick it under "Which part is it?"', why: 'production cannot add good parts to it' },
  material: { field: 'material_id', to: 'a material', pick: 'pick it under "Which material is it?"', why: 'material by weight cannot find it' },
};
export const missingLink = (i) => { const n = NEEDS_LINK[i?.kind]; return n && i[n.field] == null ? n : null; };

// A tag. What an item is reads in a regular weight; a state that needs doing
// something about (not counted yet, below minimum) stays bold (23 Oct 2026).
export const Chip = ({ color, children, title, regular = false }) => (
  <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: regular ? 400 : 700, color,
    border: `1px solid ${color}55`, background: `${color}14`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>{children}</span>
);
